import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { formValueSelector } from 'redux-form'
import { useSelector } from 'react-redux'
import { formatCurrency } from 'ggx-service/i18n/format-currency.service'
import { displayAlert } from 'ggx-componentlibrary/components/alerts/alerts.component'
import { tollwaysService } from 'ggx-src/van-booking/api/tollways.service'
import Select from 'react-select'
import { components } from 'ggx-componentlibrary/components/dropdown/dropdown.component'

import styled from 'styled-components'
import { COLOR } from 'ggx-componentlibrary/design/color/color.constants'
import { SPACING } from 'ggx-componentlibrary/design/spacing/spacing.constants'
import {
  Body,
  SmallText,
} from 'ggx-componentlibrary/design/typography/typography.styles'
import { Checkbox } from 'ggx-componentlibrary/components/checkbox/checkbox.component.js'

const StyledMenu = styled(components.Menu)`
  top: calc(100% + 8px);
  border-radius: 8px;
`

const StyledOption = styled(
  ({ className, children, innerProps, isFocused, data, isSelected }) => {
    const optionRef = React.useRef(null)
    if (isFocused && optionRef.current) {
      optionRef.current.scrollIntoView({ block: 'nearest' })
    }
    return (
      <div
        className={className}
        data-testid="dropdown-option"
        {...innerProps}
        ref={optionRef}
      >
        <Checkbox value={isSelected} />
        <div>
          <Body typographyStyles="mb-none">{children}</Body>
          {data?.dropdownValueMessage && (
            <SmallText typographyStyles="mb-none">
              {data.dropdownValueMessage}
            </SmallText>
          )}
        </div>
      </div>
    )
  }
)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  background-color: ${props =>
    props.isFocused ? COLOR.GREY_LIGHTER : 'transparent'};
  padding: ${SPACING.XS} ${SPACING.S};
  margin: 0;
  outline: none;
  cursor: pointer;
  font-weight: ${props => (props.isSelected ? 'bold' : 'normal')};
  &:hover {
    background-color: ${COLOR.GREY_LIGHTER};
  }
  ${props => {
    const hasHelperText = props?.options?.some(
      option => option.dropdownValueMessage !== undefined
    )

    return hasHelperText
      ? `
          height: 48px;
          padding: 0 ${SPACING.S};
        `
      : ''
  }}
`

const StyledValueContainer = styled(({ className, getValue, children }) => {
  const [t] = useTranslation()
  const values = getValue()
  const anyOption = values?.[0]
  return (
    <div className={className}>
      <div className="label">
        <Body typographyStyles="mb-none">
          {anyOption?.value === 'any'
            ? anyOption.label
            : t('common__tunnel', { count: values.length })}
        </Body>
        <SmallText className="hint" typographyStyles="mb-none">
          {anyOption?.value === 'any'
            ? anyOption.singleValueMessage
            : values.map(opt => opt.label).join(', ')}
        </SmallText>
      </div>
      {children}
    </div>
  )
})`
  position: relative;
  cursor: pointer;
  width: calc(100% - 24px);
  > div.label {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    overflow: hidden;
    height: 100%;
  }
  > div.label > p.hint {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
  }
`

const tunnelNameMapping = {
  any: 'common__tunnel__no_preference',
  hung: 'common__tunnel__cross-harbour-tunnel',
  western: 'common__tunnel__western_harbour_crossing',
  eastern: 'common__tunnel__eastern_harbour_crossing',
  lion_rock: 'common__tunnel__lion_rock_tunnel',
  tai_lam: 'common__tunnel__tai_lam_tunnel',
  shing_mun: 'common__tunnel__shing_mun_tunnel',
  tates_cairn: 'common__tunnel__tates_cairn_tunnel',
  eagles_nest: 'common__tunnel__eagles_nest_tunnel',
  tko: 'common__tunnel__tseung_kwan_o_tunnel',
  aberdeen: 'common__tunnel__aberdeen_tunnel',
  ckb: 'common__tunnel__ckb_tunnel',
}

const MultipleTunnelSelection = ({ name, input }) => {
  const [t] = useTranslation()
  const formSelector = formValueSelector('vanbooking-order')
  const vehicleType = useSelector(state => formSelector(state, 'vehicle'))

  useEffect(() => {
    if (!input.value) input.onChange(['any'])
  }, [])

  const defaultOption = {
    value: 'any',
    label: t('common__tunnel__no_preference'),
    singleValueMessage: t('no_preference__details'),
    dropdownValueMessage: t('no_preference__details'),
  }

  const [optionsList, setOptionsList] = useState([defaultOption])

  useEffect(() => {
    ;(async () => {
      try {
        const { values, prices } = (await tollwaysService.getTollways())[
          vehicleType
        ]
        setOptionsList([
          defaultOption,
          ...values
            .map((value, i) => ({
              value,
              label: `${t(tunnelNameMapping[values[i]])} (${formatCurrency({
                amount: prices[i],
              })})`,
            }))
            .slice(1),
        ])
      } catch {
        displayAlert({ message: t('errors.generic'), type: 'error' })
      }
    })()
  }, [vehicleType])

  return (
    <Select
      name={name}
      options={optionsList}
      value={optionsList.filter(option => input.value.includes(option.value))}
      onChange={(selected, meta) => {
        if (meta.action === 'select-option') {
          if (meta.option.value === 'any') {
            input.onChange([defaultOption.value])
          } else {
            input.onChange(
              selected.map(opt => opt.value).filter(opt => opt !== 'any')
            )
          }
        } else if (selected.length === 0) {
          input.onChange([defaultOption.value])
        } else {
          input.onChange(selected.map(opt => opt.value))
        }
      }}
      components={{
        ...components,
        Menu: StyledMenu,
        Option: StyledOption,
        ValueContainer: StyledValueContainer,
        MultiValue: () => null,
      }}
      maxMenuHeight={240}
      isMulti={true}
      isSearchable={false}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
    />
  )
}

MultipleTunnelSelection.propTypes = {
  name: PropTypes.string.isRequired,
  input: PropTypes.shape({
    value: PropTypes.string,
    onChange: PropTypes.func,
  }),
}

export { MultipleTunnelSelection }
